import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useSelector } from "react-redux";
import { type CurrentUser } from "workflowEditorV3/WorkflowEditorV3";

import { useAIPanelContext } from "components/ai-panel/AIPanelContext";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import FlexibleModal from "components/modal/FlexibleModal";
import { isKnownAuthority } from "components/workflows/workflow-editor-v3/authority";
import { isKnownFeatureLicense } from "components/workflows/workflow-editor-v3/featureLicense";
import { WorkflowEntityType } from "domain/workflows";
import { getArthurUrl, getGlobalUrl, getLaurelUrl, getOliverUrl, getStanUrl } from "services/login/endpointRepository";
import { StoreState } from "store";

type EditorEntityType = "workflow" | "template" | "subworkflow";

export type InitialTab =
    | { product: string; version: string; name: string }
    | { workflowUuid: string; product?: string; version?: string; versionId?: string }
    | { workflowJson: Record<string, unknown> };

export interface VersionRestoreParams {
    workflowUuid: string;
    versionId: string;
    editorVersion: string;
    product: string;
}

const RemoteWorkflowEditor = React.lazy(() =>
    import("workflowEditorV3/WorkflowEditorV3").then((m) => ({ default: m.WorkflowEditorV3 }))
);

const ENTITY_TYPE_MAP: Record<WorkflowEntityType, EditorEntityType> = {
    [WorkflowEntityType.WORKFLOW]: "workflow",
    [WorkflowEntityType.TEMPLATE]: "template",
    [WorkflowEntityType.SUB_WORKFLOW]: "subworkflow",
};

interface Props {
    isOpen?: boolean;
    onClose?: () => void;
    onVersionRestore?: (params: VersionRestoreParams) => void;
    initialTabs?: InitialTab[];
    entityType?: WorkflowEntityType;
    previewMode?: boolean;
}

const WorkflowEditorV3View = (props: Props) => {
    const userDetails = useSelector((state: StoreState) => state.userReducer.user);
    const { registerAiTools } = useAIPanelContext();

    const isAiToolsCapable = !props.previewMode;

    React.useEffect(() => {
        if (!isAiToolsCapable) {
            return undefined;
        }
        return () => registerAiTools(undefined);
    }, [isAiToolsCapable, registerAiTools]);

    const baseUrls = {
        oliverApiBaseUrl: getOliverUrl(),
        laurelApiBaseUrl: getLaurelUrl(),
        arthurApiBaseUrl: getArthurUrl(),
        stanApiBaseUrl: getStanUrl(),
        bmpBaseUrl: getGlobalUrl() ?? "",
    };

    const currentUser: CurrentUser | undefined = userDetails
        ? {
              uuid: userDetails.uuid,
              name: userDetails.name ?? "",
              tenantType: userDetails.tenantType,
              authorities: userDetails.role.authorities.filter(isKnownAuthority),
              featureLicenses: (userDetails.featureLicenses ?? []).filter(isKnownFeatureLicense),
          }
        : undefined;

    const editor = (
        <ErrorBoundary fallback={<div>Workflow editor failed to load.</div>}>
            <React.Suspense fallback={<LoadingIndicator />}>
                <RemoteWorkflowEditor
                    initialTabs={props.initialTabs}
                    baseUrls={baseUrls}
                    onClose={props.onClose}
                    onVersionRestore={props.onVersionRestore}
                    entityType={props.entityType !== undefined ? ENTITY_TYPE_MAP[props.entityType] : undefined}
                    previewMode={props.previewMode}
                    currentUser={currentUser}
                    registerAiTools={isAiToolsCapable ? registerAiTools : undefined}
                />
            </React.Suspense>
        </ErrorBoundary>
    );

    if (props.previewMode) {
        return editor;
    }

    return (
        <FlexibleModal
            isOpen={props.isOpen ?? false}
            hideModal={props.onClose ?? (() => undefined)}
            title=""
            onToggleFullscreen={() => undefined}
            dynamicSize={false}
            fullScreen={true}
            showHeader={false}
            disableEscapeClose={true}
        >
            {editor}
        </FlexibleModal>
    );
};

export default WorkflowEditorV3View;
